<script setup>
  import {computed, inject, ref, watch} from "vue";

  import ComponentCard from "@components/Cards/Card.vue";
  import ComponentContactCard from "@components/Cards/ContactCard.vue";
  import ComponentContactCardFavorite from "@components/Cards/ContactCardFavorite.vue";
  import ComponentConfirmationDialog from "@components/Dialogs/ConfirmationDialog.vue";
  import ComponentPatientAddressBookDialog from "@components/Dialogs/PatientAddressBookDialog.vue";
  import ComponentEmptyState from "@components/EmptyState/EmptyState.vue";
  import ComponentSelect from "@components/Selects/Select.vue";
  import {useForm} from "@inertiajs/vue3";
  import {assignmentRole} from "@pages/Contact/Enums/Enums.js";
  import {MedButton, MedInfoButton, MedLink} from "@pharma4u/medicheck-components";

  defineOptions({
    name: "PatientsPatientContactsData",
  });

  const props = defineProps({
    patient: {
      type: Object,
      required: true,
    },
    withinDialog: {
      type: Boolean,
      default: false,
    },
    pendingContact: {
      type: Object,
      default: null,
    },
  });

  const broadcast = inject("$broadcast");

  const refPatientAddressBookDialog = ref(null);
  const refConfirmationDialog = ref(null);

  const internalPendingContact = ref(props.pendingContact);
  const activeEditAddress = ref();

  /**
   * Hide internalPendingContact immediately, if it is
   * found within patients.contact.
   */
  const contactsDoesNotContainInternalPendingContact = computed(() => {
    return (
      (props.patient?.contacts ?? []).filter((contact) => {
        let searchString = [contact.firstname, contact.lastname, contact.company].join(" ").toLowerCase();

        return [
          internalPendingContact.value.firstname,
          internalPendingContact.value.lastname,
          internalPendingContact.value.company,
        ]
          .join(" ")
          .replace("Dr.", "")
          .replace("Prof.", "")
          .replace("Med.", "")
          .toLowerCase()
          .split(" ")
          .every((word) => searchString.includes(word));
      }).length === 0
    );
  });

  const form = useForm({
    role: null,
  });

  const editForm = useForm({
    role: null,
  });

  watch(
    () => props.pendingContact,
    (newValue) => {
      internalPendingContact.value = newValue;
    },
  );

  function confirmRemove(contactId) {
    refConfirmationDialog.value.open(contactId);
  }

  function remove(contactId) {
    form.delete(route("assignment.destroy", {patient: props.patient.id, contact: contactId}), {
      preserveScroll: true,
      onSuccess: () => {
        broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
      },
    });
  }

  const openAddressBook = (search) => {
    refPatientAddressBookDialog.value.open(search);
  };

  function edit(contact) {
    editForm.role = contact.role;
    activeEditAddress.value = contact.id;
  }

  function cancelEdit() {
    activeEditAddress.value = null;
  }

  function update() {
    editForm.patch(route("assignment.update", {patient: props.patient.id, contact: activeEditAddress.value}), {
      preserveScroll: true,
      onSuccess: () => {
        activeEditAddress.value = null;
        broadcast.patient.postMessage(props.patient.id, {action: "reload.patient"});
      },
    });
  }

  defineExpose({
    openAddressBook,
  });
</script>

<template>
  <ComponentCard
    id="patient-contacts-data"
    title="Kontakte"
    subtitle="Ärzte, Pflegedienste oder Heime"
    :class="{
      'rounded-lg border-2 border-gray-200': !withinDialog,
      '-m-4 shadow-none!': withinDialog,
      'animate-none!': patient?.contacts?.length,
    }"
    :test-id="getTestId('patient-contact')"
  >
    <template #button>
      <div class="ml-auto">
        <MedButton :data-test="getTestId('patient-contact-card-open-address-dialog-button')" @click="openAddressBook()">
          Hinzufügen
        </MedButton>
      </div>
    </template>

    <ol v-if="patient?.contacts?.length || internalPendingContact" class="grid grid-cols-1 gap-8 sm:grid-cols-2">
      <li v-if="internalPendingContact && contactsDoesNotContainInternalPendingContact" class="relative bg-mcred-50">
        <div class="px-2 pt-2">
          <div class="flex items-center space-x-2">
            <h6 class="text-xs">
              <MedLink
                as="button"
                variant="primary"
                :data-test="getTestId('contacts-wawi-link')"
                @click.prevent="openAddressBook(internalPendingContact)"
              >
                Kontakt aus WaWi
              </MedLink>
            </h6>

            <MedInfoButton
              size="md"
              side="right"
              aria-label="WaWi Kontakt Info"
              :data-test="getTestId('contacts-wawi-info-button')"
            >
              <p class="text-sm">
                Der folgende Kontakt wurde aus der Warenwirtschaft übertragen und ist nur in dieser Analyse verfügbar.
                Sie können den Kontakt übergreifend zu Ihrem Patienten speichern.
              </p>
            </MedInfoButton>
          </div>
        </div>

        <ComponentContactCard :contact="internalPendingContact" :small="true" />

        <MedButton
          :aria-label="
            internalPendingContact.lastname
              ? `Kontakt ${internalPendingContact.lastname} hinzufügen`
              : 'Kontakt aus Warenwirtschaft hinzufügen'
          "
          icon="add"
          class="absolute right-2 bottom-2"
          rounded="full"
          variant="primary"
          :data-test="getTestId('patient-contact-card-open-internal-address-dialog-button')"
          @click="openAddressBook(internalPendingContact)"
        />
      </li>

      <li v-for="contact in patient.contacts" :key="contact.id" class="group relative">
        <ComponentContactCard class="h-full min-h-44 hover:bg-gray-100" :contact="contact" />

        <ComponentContactCardFavorite :contact="contact" :patient="patient" />

        <div class="absolute right-2 bottom-2 flex gap-2">
          <MedButton
            :aria-label="contact.lastname ? `Kontakt ${contact.lastname} bearbeiten` : 'Kontakt bearbeiten'"
            flat
            icon="edit"
            rounded="full"
            variant="primary"
            :data-test="getTestId('contact-card-edit-button')"
            @click="edit(contact)"
          />

          <MedButton
            :aria-label="contact.lastname ? `Kontakt ${contact.lastname} entfernen` : 'Kontakt entfernen'"
            flat
            icon="delete"
            rounded="full"
            :data-test="getTestId('contact-card-delete-button')"
            @click="confirmRemove(contact.id)"
          />
        </div>

        <div v-if="activeEditAddress === contact.id" class="absolute top-0 left-0 size-full bg-white p-2">
          <ComponentSelect
            v-model="editForm.role"
            class="mt-4"
            label="Funktion (optional)"
            :options="assignmentRole"
            :nullable="true"
            placeholder-text=""
            helper-text="Welche Rolle nimmt dieser Kontakt für den Patienten ein?"
            :test-id="getTestId('patient-contact-card-role')"
          />

          <div class="mt-4 flex flex-row-reverse space-x-4 space-x-reverse">
            <MedButton
              variant="primary"
              :disabled="editForm.processing"
              :data-test="getTestId('patient-contact-card-save-button')"
              @click="update()"
            >
              Speichern
            </MedButton>

            <MedButton :data-test="getTestId('patient-contact-card-cancel-button')" @click="cancelEdit()">
              Abbrechen
            </MedButton>
          </div>
        </div>
      </li>
    </ol>

    <ComponentEmptyState v-else :test-id="getTestId('patient-contact-card')">
      Sie können bestehende Adressen von Ärzten, Pflegediensten oder Heimen aus Ihrem Adressbuch zu diesem Patienten
      hinzufügen.
    </ComponentEmptyState>

    <ComponentPatientAddressBookDialog ref="refPatientAddressBookDialog" :patient="patient" />

    <ComponentConfirmationDialog
      ref="refConfirmationDialog"
      title="Kontakt entfernen?"
      content="Die Adresse bleibt unverändert in Ihrem Adressbuch."
      @confirmed="(contactId) => remove(contactId)"
    />
  </ComponentCard>
</template>
